import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, MapPin, MonitorSmartphone, Users, Loader2, CheckCircle2, Ticket } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { EV } from "@/pages/eventsText";
import { TZ, localTz } from "@/lib/schedule";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const LOC = { hu: "hu-HU", en: "en-GB", de: "de-DE", fr: "fr-FR", cs: "cs-CZ" };
const fmtRange = (s, e, lang, timeZone) => {
  const o = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone };
  const a = new Date(s), b = new Date(e);
  const same = a.toLocaleDateString("en-CA", { timeZone }) === b.toLocaleDateString("en-CA", { timeZone });
  return `${a.toLocaleString(LOC[lang], o)} – ${same ? b.toLocaleTimeString(LOC[lang], { hour: "2-digit", minute: "2-digit", timeZone }) : b.toLocaleString(LOC[lang], o)}`;
};
const linkify = (txt, href) => {
  const [pre, rest] = txt.split("<a>");
  const [inner, post] = (rest || "").split("</a>");
  return <>{pre}<a href={href} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{inner}</a>{post}</>;
};

function RegisterDialog({ ev, onClose, onDone, x, lang }) {
  const [email, setEmail] = useState("");
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${ev.id}/register`, { email, accept_terms: terms, accept_privacy: privacy, lang });
      setDone(true);
      onDone();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cb = "mt-0.5 border-zinc-700 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-[#0a0d18] text-zinc-100 sm:max-w-md" data-testid="event-register-dialog">
        <DialogHeader>
          <DialogTitle className="text-left text-xl">{ev.title}</DialogTitle>
          <DialogDescription className="text-left text-zinc-400">{fmtRange(ev.starts_at, ev.ends_at, lang)} ({localTz()})</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-[14px] text-emerald-200" data-testid="event-register-done"><CheckCircle2 size={18} className="shrink-0" /> {x.done}</div>
        ) : (
          <div className="space-y-3">
            <Input data-testid="event-register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={x.email} className="h-11 border-zinc-800 bg-zinc-950" />
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-zinc-400"><Checkbox data-testid="event-accept-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className={cb} /><span>{linkify(x.terms, "/terms")}</span></label>
            <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-zinc-400"><Checkbox data-testid="event-accept-privacy" checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} className={cb} /><span>{linkify(x.privacy, "/privacy")}</span></label>
            <button data-testid="event-register-submit" onClick={submit} disabled={busy || !terms || !privacy || !/\S+@\S+\.\S+/.test(email)}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-[14px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
              {busy && <Loader2 size={15} className="animate-spin" />} {x.submit}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ ev, x, lang, onRegister }) {
  const start = new Date(ev.starts_at);
  const ongoing = start <= new Date();
  const blocked = ev.full || !ev.registration_open;
  return (
    <article className="flex gap-4 rounded-xl border border-zinc-800/70 bg-[#0d1120] p-5 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-blue-500/40" data-testid={`event-card-${ev.id}`}>
      <div className="grid h-16 w-14 shrink-0 place-items-center rounded-lg border border-[#FFCC00]/30 bg-[#FFCC00]/10 text-center leading-tight">
        <div><div className="text-[10px] font-bold uppercase text-[#FFCC00]">{start.toLocaleString(LOC[lang], { month: "short" })}</div><div className="text-2xl font-bold text-zinc-50">{start.getDate()}</div></div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[16px] font-semibold text-zinc-50">{ev.title}</h3>
          {ongoing && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />{x.ongoing}</span>}
        </div>
        {ev.description && <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-zinc-400">{ev.description}</p>}
        <div className="mt-3 space-y-1 text-[12px] text-zinc-300">
          <div className="flex items-center gap-1.5" data-testid={`event-when-${ev.id}`}><CalendarDays size={13} className="text-zinc-500" />{fmtRange(ev.starts_at, ev.ends_at, lang)} <span className="text-zinc-500">· {x.cet}: {fmtRange(ev.starts_at, ev.ends_at, lang, TZ)}</span></div>
          {ev.location && <div className="flex items-center gap-1.5"><MapPin size={13} className="text-zinc-500" />{ev.location}</div>}
          {ev.platform && <div className="flex items-center gap-1.5"><MonitorSmartphone size={13} className="text-zinc-500" />{ev.platform}</div>}
          {ev.agents?.length > 0 && <div className="flex flex-wrap items-center gap-1.5"><Users size={13} className="text-zinc-500" />{ev.agents.map((a) => <span key={a} className="rounded-full border border-blue-500/30 bg-blue-600/10 px-2 py-0.5 text-[11px] text-blue-200">{a}</span>)}</div>}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button data-testid={`event-register-${ev.id}`} onClick={() => onRegister(ev)} disabled={blocked}
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500">
            <Ticket size={14} /> {ev.full ? x.full : !ev.registration_open ? x.closed : x.register}
          </button>
          <span className="text-[12px] text-zinc-500" data-testid={`event-seats-${ev.id}`}>{ev.capacity ? x.seats.replace("{n}", ev.registered).replace("{c}", ev.capacity) : x.registered.replace("{n}", ev.registered)}</span>
        </div>
      </div>
    </article>
  );
}

export function EventsSection({ limit, compact = false }) {
  const { lang } = useLang();
  const x = EV[lang] || EV.en;
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/events").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (compact && !list?.length) return null;
  const shown = limit ? list?.slice(0, limit) : list;
  return (
    <section className={compact ? "mt-8" : "mt-12"} id={compact ? "events-home" : "events"} data-testid={compact ? "events-home" : "events-section"}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFCC00]">{x.kicker}</div>
      <h2 className={`mt-1 font-bold tracking-tight text-zinc-50 ${compact ? "text-lg" : "text-2xl"}`}>{x.title}</h2>
      {!compact && <p className="mt-1 text-[14px] text-zinc-400">{x.intro}</p>}
      <div className={`mt-5 grid gap-3 ${compact ? "" : "lg:grid-cols-2"}`}>
        {list === null && <Loader2 className="animate-spin text-zinc-500" />}
        {list?.length === 0 && <p className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-5 text-[14px] text-zinc-500" data-testid="events-empty">{x.none}</p>}
        {shown?.map((ev) => <EventCard key={ev.id} ev={ev} x={x} lang={lang} onRegister={setSel} />)}
      </div>
      {compact && list.length > shown.length && <a href="/availability#events" data-testid="events-home-more" className="mt-3 inline-block text-[13px] text-blue-400 hover:underline">+{list.length - shown.length} →</a>}
      {sel && <RegisterDialog ev={sel} x={x} lang={lang} onClose={() => setSel(null)} onDone={load} />}
    </section>
  );
}
